import { type JSX, Show } from 'solid-js';
import { t } from '../../i18n';
import { controlKey } from '../../lib/card-controls';
import { useCardControls } from '../../lib/card-controls-context';
import { isSaving, wantedValue } from '../../lib/flood-guard-context';
import type { CardEpicRow, CardState } from '../../lib/machine-card';
import { ControlButton } from './ControlButton';

// One epic's Pause (or Resume) and Stop (#6753, prototype epicCtl, `:2339`): on an epic
// with a running slice or a pause mark, on a machine that is Autonomous or Needs you, for an
// admin on this org's machine. Named by the epic's title, never a number ("Pause epic Billing
// webhooks overhaul"): the wire carries no epic number.

export function EpicControls(props: {
  readonly epic: CardEpicRow;
  readonly state: CardState;
}): JSX.Element {
  const handle = useCardControls();
  const shown = () =>
    handle?.on() === true && handle.epics.controllable(props.epic, props.state) ? handle : null;
  return (
    <Show when={shown()}>
      {(h) => {
        const id = () => props.epic.plan_id;
        const title = () => props.epic.title;
        const page = () => h().card.page;
        const saving = (): boolean =>
          isSaving(page(), controlKey.epicPause(id())) ||
          isSaving(page(), controlKey.epicStop(id()));
        const held = (): boolean => saving() || h().card.locked() > 0;
        const stopWaiting = (): boolean =>
          wantedValue(page(), controlKey.epicStop(id())) !== undefined;
        const resuming = (): boolean => h().epics.value(props.epic) === 'pause';
        return (
          <span class="mission-ctl">
            <Show when={saving()}>
              <span class="mission-ctl__saving">{t('fleet.machines.ctl_saving')}</span>
            </Show>
            {/* One button flipping between Pause and Resume, so focus stays on it. */}
            <ControlButton
              glyph={resuming() ? 'play' : 'pause'}
              name={t(
                resuming() ? 'fleet.machines.ctl_epic_resume' : 'fleet.machines.ctl_epic_pause',
                { title: title() },
              )}
              word={t(resuming() ? 'fleet.machines.ctl_resume' : 'fleet.machines.ctl_pause')}
              held={held()}
              busy={saving()}
              onPress={() => h().epics.toggle(props.epic)}
            />
            <ControlButton
              glyph="stop"
              name={t('fleet.machines.ctl_epic_stop', { title: title() })}
              word={t('fleet.machines.ctl_stop')}
              held={held() || stopWaiting()}
              busy={saving()}
              stopFor={id()}
              onPress={() => h().epics.askStop(props.epic)}
            />
          </span>
        );
      }}
    </Show>
  );
}
